/**
 * Landing / install screen — the first thing a phone visitor sees.
 *
 * If already running as an installed PWA (display-mode: standalone) the app
 * skips this. Otherwise it offers install guidance TAILORED TO THE CURRENT
 * DEVICE, so an iPhone never sees Android steps and vice versa:
 *   - Any browser that fires `beforeinstallprompt` (Android/desktop Chrome,
 *     Edge) gets a one-tap native "Install Fume" button.
 *   - iOS (no install event exists) gets the Safari Share -> Add to Home
 *     Screen steps.
 *   - Android without the event (already dismissed, non-Chrome) gets the
 *     Chrome menu steps.
 *   - Desktop without the event gets address-bar / menu steps.
 *
 * Android installs can trip a Google Play Protect "unsafe app blocked" warning
 * (it targets the WebAPK wrapper Android mints, not Fume). We surface an
 * Android-only, reassuring, expandable note explaining the app is safe and how
 * to clear the warning. "Continue in browser" proceeds either way.
 */
import { useEffect, useState } from 'react';
import { VersionTag } from './VersionTag.js';

interface BipEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
}

type Platform = 'ios' | 'android' | 'desktop';

export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function getPlatform(): Platform {
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as a Mac; catch it via touch points.
  const iPadOS = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  if (/iphone|ipad|ipod/i.test(ua) || iPadOS) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
}

export function Landing(props: { onContinue: () => void }): JSX.Element {
  const [bip, setBip] = useState<BipEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBip = (e: Event): void => {
      e.preventDefault();
      setBip(e as BipEvent);
    };
    const onInstalled = (): void => setInstalled(true);
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async (): Promise<void> => {
    if (!bip) return;
    await bip.prompt();
    await bip.userChoice;
    setBip(null);
  };

  const platform = getPlatform();

  function InstallGuidance(): JSX.Element {
    if (installed) {
      return <p className="hint">Installed — open Fume from your home screen.</p>;
    }
    // Best path: the browser can install directly (Android/desktop Chrome, Edge).
    if (bip) {
      return (
        <>
          <button type="button" className="primary-cta" onClick={() => void install()}>
            Install Fume
          </button>
          <p className="hint">One tap — then open Fume from your home screen.</p>
        </>
      );
    }
    if (platform === 'ios') {
      return (
        <ol className="steps">
          <li>
            In <strong>Safari</strong>, tap the <strong>Share</strong> icon (the square with an
            up-arrow).
          </li>
          <li>
            Scroll down and choose <strong>Add to Home Screen</strong>.
          </li>
          <li>
            Tap <strong>Add</strong>, then open Fume from your home screen — it runs full-screen and
            offline.
          </li>
        </ol>
      );
    }
    if (platform === 'android') {
      return (
        <ol className="steps">
          <li>
            In <strong>Chrome</strong>, tap the <strong>&#8942;</strong> menu (top-right).
          </li>
          <li>
            Choose <strong>Install app</strong> (or <strong>Add to Home screen</strong>).
          </li>
          <li>
            Confirm, then open Fume from your home screen — it runs full-screen and offline.
          </li>
        </ol>
      );
    }
    return (
      <ol className="steps">
        <li>
          Click the <strong>install icon</strong> in the address bar, or open your browser menu.
        </li>
        <li>
          Choose <strong>Install Fume</strong> (Chrome / Edge) or <strong>Add to Home Screen</strong>.
        </li>
        <li>Launch Fume from your apps — it opens in its own window.</li>
      </ol>
    );
  }

  function AndroidWarningNote(): JSX.Element {
    return (
      <details className="apk-note">
        <summary>
          <span className="apk-note-lead">
            Android may warn "unsafe app blocked" — Fume is safe with or without it.
          </span>
          <span className="apk-note-cta">How to clear the warning</span>
        </summary>
        <div className="apk-note-body">
          <p className="hint">
            That warning is a Google Play Protect check on the install wrapper Android builds
            around the app — not on Fume itself. Nothing changes about how the app runs. You can
            tap <strong>More details → Install anyway</strong>, or clear it for good:
          </p>
          <ol className="steps">
            <li>
              Update <strong>Chrome</strong>, <strong>Google Play Services</strong>, and{' '}
              <strong>Android System WebView</strong> in the Play Store.
            </li>
            <li>Restart the phone.</li>
            <li>Install Fume again.</li>
          </ol>
          <p className="hint">
            Don't want to install at all? <strong>Continue in browser</strong> below runs the full
            app — nothing is lost.
          </p>
        </div>
      </details>
    );
  }

  const cardHeading =
    platform === 'ios' || platform === 'android' ? 'Install to your home screen' : 'Install Fume';

  return (
    <main className="landing">
      <div className="landing-hero">
        <img className="hero-mark" src="/icons/hero-mark.png" alt="Fume" />
        <p className="eyebrow">Device-first ephemeral secret exchange</p>
        <h1>Fume</h1>
        <p className="tagline">
          Send a secret from your phone. It travels peer-to-peer, is gone at read-time, and leaves
          no trace on any server.
        </p>
      </div>

      <div className="landing-card">
        <h2>{cardHeading}</h2>
        <InstallGuidance />
        {platform === 'android' && <AndroidWarningNote />}
      </div>

      <button type="button" className="ghost-cta" onClick={props.onContinue}>
        Continue in browser &rarr;
      </button>

      <ul className="landing-points">
        <li>🔒 Never written to storage</li>
        <li>🔗 Peer-to-peer (WebRTC)</li>
        <li>⏳ Self-destructs on a TTL</li>
      </ul>

      <a className="what-link" href="/what-is-fume">What is Fume?</a>

      <VersionTag />
    </main>
  );
}
